/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */

import { useAuth0 } from '@auth0/auth0-react';
import {
  alpha,
  styled,
  useColorScheme,
} from '@mui/material/styles';
import { createBrowserHistory } from 'history';
import PropTypes from 'prop-types';
import * as React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Icon,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Popover,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';

import { showNotifications } from '@pingum/app-state-management/app/actions';

import {
  AccessTime,
  AccountCircle,
  AdminPanelSettings,
  Business,
  Chat,
  Close,
  DarkMode,
  EditNotifications,
  Help,
  Launch,
  Logout,
  Notifications,
  Settings,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { Chat as StreamChat } from 'stream-chat-react';
import { getBarnNotices } from '../../redux/barn/actions';
import { searchQuestri } from '../../redux/search/actions';

import NotificationList from '../../components/NotificationList';
import QuestriLogo from '../../components/QuestriLogo';
import SearchList from '../../components/SearchList';
import User from '../../components/User';
import { ChannelContainer } from '../../stream/components/ChannelContainer/ChannelContainer';
import { Sidebar } from '../../stream/components/Sidebar/Sidebar';
import { WorkspaceController } from '../../stream/context/WorkspaceController';
import { daysRemaining } from '../../utils/dateHelper';
import appTheme from '../../utils/theme';
import { gray } from '../../utils/themePrimitives';
import useDebounce from '../../utils/useDebounce';
import { AppBar } from './styles';

export const history = createBrowserHistory();

const getDomainWithoutSubdomain = (url) => {
  const urlParts = new URL(url).hostname.split('.')

  return urlParts
    .slice(0)
    .slice(-(urlParts.length === 4
      ? 3
      : 2))
    .join('.')
}

export const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.25) },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: 0.5,
    width: 'auto',
  },
}));

export const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const StyledInputBase = styled(InputBase)(({
  theme,
  color = 'white',
}) => ({
  color,
  '& input::placeholder': { color: 'white' },
  fontSize: '.8rem',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': { width: '20ch' },
    },
  },
}));

const AppBarComponent = ({
  isLoading,
  tenantObject,
  navigate,
  chatClient,
  unreadCount,
}) => {
  const dispatch = useDispatch();
  const {
    mode,
    setMode,
  } = useColorScheme();
  const fullScreen = useMediaQuery(appTheme.breakpoints.down('md'));
  const isUnder1400 = useMediaQuery('(max-width:1400px)');
  const isUnder1200 = useMediaQuery('(max-width:1200px)');
  const isUnder1100 = useMediaQuery('(max-width:1100px)');
  const isUnder900 = useMediaQuery('(max-width:900px)');
  const isUnder1000 = useMediaQuery('(max-width:1000px)');

  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebounce(search, 1000);
  const sortModel = React.useMemo(() => [], []);
  const [onlySearchFiles, setOnlySearchFiles] = React.useState(false);
  const [searchHorses, setSearchHorses] = React.useState(true);
  const [searchUsers, setSearchUsers] = React.useState(true);
  const [searchNotes, setSearchNotes] = React.useState(true);

  const additionalSearchQueryParams = React.useMemo(() => {
    if (onlySearchFiles) {
      return '&onlyFiles=true&includeHorses=false&includeNotes=true&includeUsers=false';
    }
    return [
      `&includeHorses=${searchHorses || false}`,
      `&includeUsers=${searchUsers || false}`,
      `&includeNotes=${searchNotes || false}`,
      '&onlyFiles=false',
    ].join('');
  }, [onlySearchFiles, searchHorses, searchUsers, searchNotes]);

  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [notificationAnchorElement, setNotificationAnchorElement] = React.useState(null);
  const [searchAnchorElement, setSearchAnchorElement] = React.useState(null);
  const [showChat, setShowChat] = React.useState(false);
  const [chatAnchorEl, setChatAnchorEl] = React.useState(null);

  React.useEffect(() => {
    if (debouncedSearch) {
      dispatch(searchQuestri(
        debouncedSearch,
        additionalSearchQueryParams,
        0,
        50,
        sortModel.map((s) => `&sort=${s.field},${s.sort}`),
      ));
    }
  }, [additionalSearchQueryParams, debouncedSearch, dispatch, sortModel]);

  const {
    isAuthenticated,
    loginWithRedirect,
    user: authUser,
  } = useAuth0();
  const currentRoute = useLocation();

  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  // const barnNotices = useSelector((state) => state.barn.barnNotices);
  const permissions = useSelector((state) => state.user.permissions);
  const isClient = useSelector((state) => state.user.isClient);
  const barnUser = useSelector((state) => state.user.barnUser);
  const isFetchingUser = useSelector((state) => state.user.isFetchingUser);
  const selectedLocation = useSelector((state) => state.location.selectedLocation);
  const unviewedNotificationCount = useSelector((state) => state.userNotification.unviewedNotificationCount);
  const myTimezone = useSelector((state) => state.app.myTimezone);
  const contextTimezone = useSelector((state) => state.app.contextTimezone);
  const canAccessFullApp = permissions.owner || permissions.admin || permissions.staff;
  const canAccessBoardOnly = isClient && !canAccessFullApp;

  const fullAccessPages = [
    {
      title: 'Board',
      path: '/board',
      // startIcon: <Monitor />,
    },
    {
      title: 'Calendar',
      path: '/calendar',
      // startIcon: <CalendarMonth />,
    },
    {
      title: 'Horses',
      path: '/horses',
      // startIcon: <HorseIcon color="#fff" fontSize={18} />,
      // badgeCount: barnNotices.missingLocations.length + barnNotices.missingFeed.length + barnNotices.horseLodgingRequests.length,
    },
    {
      title: 'Tasks',
      path: '/tasks',
    },
    {
      title: 'Horse Billing',
      path: '/billing/dashboard',
    },
    // {
    //   title: 'More',
    //   endIcon: <KeyboardArrowDown />,
    //   onClick: (e) => { setShowMoreAnchorEl(e.currentTarget); },
    // },
  ];
  const pages = canAccessFullApp
    ? fullAccessPages
    : [];

  let settings = [{
    name: 'Profile',
    path: '/settings/profile',
    icon: <AccountCircle color="secondary" />,
  }, {
    name: 'Notification Settings',
    path: '/settings/notifications',
    divider: true,
    icon: <EditNotifications color="secondary" />,
  }];
  if (permissions.admin || permissions.owner) {
    settings = [...settings, {
      name: 'Questri Settings',
      path: '/settings/feeds',
      divider: true,
      icon: <Settings color="secondary" />,
    }]
  }
  if (permissions.owner) {
    settings = [...settings, {
      name: 'Administrative & Billing',
      path: '/settings/admin',
      divider: true,
      icon: <AdminPanelSettings color="secondary" />,
    }]
  }
  settings = [...settings, {
    name: 'Help - Get in Contact',
    path: null,
    href: 'https://getquestri.com/contact-us',
    target: '_blank',
    icon: <Help color="secondary" />,
    showOnHome: true,
  }, {
    name: 'Switch Questri Barn',
    path: null,
    href: `${window.location.protocol}//${getDomainWithoutSubdomain(window.location.href)}`,

    icon: <Business color="secondary" />,
  }, {
    name: 'Logout',
    path: '/logout',
    icon: <Logout color="secondary" />,
    showOnHome: true,
  }]
  const [profileSetting, ...otherSettings] = settings;

  const isPageSelected = (pagePath) => {
    const windowPath = window.location.pathname;
    if (pagePath === '/board' && (windowPath.startsWith('/board'))) {
      return true;
    }
    if (pagePath === '/horses' && windowPath.startsWith('/horses')) {
      return true;
    }
    if ((pagePath === '/calendar' && (windowPath.startsWith('/calendar') || windowPath === '/' || windowPath === ''))) {
      return true;
    }
    if (!pagePath && !windowPath.startsWith('/board') && !windowPath.startsWith('/horses') && !windowPath.startsWith('/calendar') && windowPath !== '/' && windowPath !== '') {
      return true;
    }
    if (pagePath === '/tasks/planning' && (windowPath === '/' || windowPath.startsWith('/tasks'))) {
      return true;
    }
    if (pagePath === '/tasks' && windowPath.startsWith('/tasks')) {
      return true;
    }
    if ((pagePath === '/billing' || pagePath === '/billing/dashboard' || pagePath === '/billing/billables' || pagePath === '/billing/upcoming') && windowPath.startsWith('/billing')) {
      return true;
    }
    if (pagePath === '/settings/feeds' && windowPath.startsWith('/settings')) {
      return true;
    }
    return false;
  };

  const handleGoToChatPage = () => {
    setChatAnchorEl(null);
    setShowChat(false);
    setTimeout(() => {
      navigate('/chat')
    }, 1000)
  };
  const handleClose = () => {
    setChatAnchorEl(null);
    setShowChat(false);
  };

  React.useEffect(() => {
    if (selectedBarn && selectedBarn.id) {
      dispatch(getBarnNotices(selectedBarn.id));
    }
  }, [selectedBarn]);

  React.useEffect(() => {
    setAnchorElUser(null);
    setAnchorElNav(null);
  }, [currentRoute.pathname]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleUserMenuSelection = (event, setting) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Close the menus immediately
    setAnchorElUser(null);
    setAnchorElNav(null);

    // Wait 250ms for the closing animation to finish before navigating
    setTimeout(() => {
      if (setting.path) {
        navigate(setting.path);
        return;
      }
      if (setting.href) {
        if (setting.target === '_blank') {
          window.open(setting.href, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = setting.href;
        }
      }
    }, 350);
  };

  const handleNotificationButtonClick = (e) => {
    setNotificationAnchorElement(e.target);
    dispatch(showNotifications());
  };

  let trialDaysRemaining = null;
  if (tenantObject.freeTrialEndDate) {
    const freeTrialEndTime = new Date(tenantObject.freeTrialEndDate).getTime();
    if (freeTrialEndTime > new Date().getTime()) {
      trialDaysRemaining = daysRemaining(tenantObject.freeTrialEndDate || 0);
    }
  }

  let trialLabel = 'Trial - ending today';
  let minimalTrialLabel = 'ending today';
  let tinyTrialLabel = 'ending';
  if (trialDaysRemaining !== null) {
    trialLabel = `Trial - ${trialDaysRemaining} days remaining`;
    minimalTrialLabel = `${trialDaysRemaining} days`;
    tinyTrialLabel = `${trialDaysRemaining}d`;
  }

  let navGap = 1;
  let navButtonPaddingX = 1.5;
  let navRightMargin = '1rem';
  let trialGroupGap = 2;
  let trialChipPaddingX = 1.5;
  let trialChipPaddingY = 0.8;
  let trialChipFontSize = '0.9rem';
  if (isUnder1400) {
    navGap = 0.5;
    navButtonPaddingX = 1;
    navRightMargin = '0.5rem';
    trialGroupGap = 1;
    trialChipPaddingX = 1.25;
    trialChipPaddingY = 0.75;
    trialChipFontSize = '0.85rem';
  }

  const cstCompact = isUnder1100;
  let cstLabel = moment.tz(contextTimezone).zoneAbbr();
  if (cstCompact) {
    cstLabel = '';
  }

  let cstChipSize = 'medium';
  if (isUnder1200) {
    cstChipSize = 'small';
  }

  let cstContainerDisplay = 'inline-flex';
  let cstContainerJustify = 'flex-start';
  let cstContainerWidth = 'auto';
  let cstContainerHeight = 'auto';
  let cstContainerPadding = '6px 12px';
  let cstContainerRadius = '16px';
  let cstChipMinWidth = 'auto';
  let cstChipPaddingX = 0.5;
  let cstChipPaddingY = 0.25;
  let cstChipPadding;
  let cstChipRadius = '16px';
  let cstChipColumnGap = 0.75;
  let cstChipLabelDisplay = 'inline';
  let cstChipLabelFontSize = '0.75rem';
  if (cstCompact) {
    cstContainerDisplay = 'flex';
    cstContainerJustify = 'center';
    cstContainerWidth = 32;
    cstContainerHeight = 32;
    cstContainerPadding = 0;
    cstContainerRadius = '50%';
    cstChipMinWidth = 32;
    cstChipPaddingX = 0;
    cstChipPaddingY = 0;
    cstChipPadding = 0;
    cstChipRadius = '50%';
    cstChipColumnGap = 0;
    cstChipLabelDisplay = 'none';
  }
  if (isUnder1200) {
    cstChipLabelFontSize = '0.7rem';
  }

  let hoverText = {};
  if (mode !== 'dark') {
    hoverText = { color: 'black' };
  }
  let trialChipLabel = trialLabel;
  if (isUnder1200) {
    trialChipLabel = minimalTrialLabel;
  }
  if (isUnder900) {
    trialChipLabel = tinyTrialLabel;
  }
  let trialChipColor = 'warning';
  if (trialDaysRemaining !== null && trialDaysRemaining > 3) {
    trialChipColor = 'info';
  }
  const showClientBanner = isAuthenticated && canAccessBoardOnly && !!(selectedBarn && selectedBarn.id);

  return (
    <AppBar
      position="fixed"
      sx={{ zIndex: (theme) => (theme.zIndex.drawer || 1200) + 10 }}
    >
      <Container maxWidth="100%">
        <Toolbar disableGutters variant="dense">
          <Box sx={{ '&:hover': { cursor: 'pointer' } }} onClick={() => navigate('/board')}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                mr: 2,
                display: {
                  xs: 'none',
                  md: 'flex',
                },
              }}
            >
              <QuestriLogo removePadding color="#fff" fontSize={30} />
            </Typography>
          </Box>

          {
            isAuthenticated
            && (

              <Box
                sx={{
                  flexGrow: 1,
                  display: {
                    xs: 'flex',
                    md: 'none',
                  },
                }}
              >
                <IconButton
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  style={{
                    color: 'white',
                    '&:hover': hoverText,
                  }}
                >
                  <MenuIcon />
                </IconButton>

                {
                  anchorElNav && (
                    <Menu
                      id="menu-appbar"
                      anchorEl={anchorElNav}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      open={Boolean(anchorElNav)}
                      onClose={handleCloseNavMenu}
                      sx={{
                        display: {
                          xs: 'block',
                          md: 'none',
                        },
                      }}
                    >
                      {(canAccessFullApp || canAccessBoardOnly) && (
                        [...pages.filter((p) => p.title !== 'More')].map((page) => {
                          const pageIsSelected = isPageSelected(page.path);
                          let pageTitleStyle = { fontWeight: 'normal' };
                          if (pageIsSelected) {
                            pageTitleStyle = { fontWeight: 'bold' };
                          }

                          return (
                            <MenuItem
                              key={page.title}
                              ref={page.ref}
                              onClick={() => {
                                handleCloseNavMenu();
                                setTimeout(() => {
                                  if (page.onClick) {
                                    page.onClick()
                                  } else {
                                    navigate(page.path);
                                  }
                                }, 250)
                              }}
                            >
                              <Badge
                                badgeContent={page.badgeCount}
                                color="error"
                                anchorOrigin={{
                                  vertical: 'top',
                                  horizontal: 'right',
                                }}
                              >
                                <Typography
                                  textAlign="center"
                                  sx={pageTitleStyle}
                                >
                                  {page.title}
                                </Typography>
                              </Badge>
                            </MenuItem>
                          );
                        })
                      )}
                    </Menu>
                  )
                }
              </Box>
            )
          }
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              display: {
                xs: 'flex',
                md: 'none',
              },
            }}
          >
            <QuestriLogo removePadding color="#fff" />
          </Typography>

          {
            !isAuthenticated
              ? (
                <>
                  <Box sx={{ flex: 1 }} />
                  <Box sx={{ flexGrow: 0 }}>

                    <Button
                      color="secondary"
                      sx={{ mr: 2 }}
                      disabled={isLoading}
                      onClick={() => {
                        let returnPath = window.location.pathname;
                        if (window.location.pathname.indexOf('logout') >= 0) {
                          returnPath = '/';
                        }
                        loginWithRedirect({ appState: { returnTo: returnPath + window.location.search } });
                      }}
                      variant="contained"
                    >
                      Log In
                    </Button>
                    <Button
                      color="secondary"
                      disabled={isLoading}
                      onClick={() => {
                        let returnPath = window.location.pathname;
                        if (window.location.pathname.indexOf('logout') >= 0) {
                          returnPath = '/';
                        }
                        loginWithRedirect({
                          screen_hint: 'signup',
                          ui_locales: 'es',
                          mode: 'signUp',
                          appState: { returnTo: returnPath + window.location.search },
                          authorizationParams: { screen_hint: 'signup' },
                        });
                      }}
                      variant="outlined"
                    >
                      Sign Up
                    </Button>
                  </Box>
                </>
              )
              : (
                <>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: {
                        xs: 'none',
                        md: 'flex',
                      },
                      gap: navGap,
                      alignItems: 'center',
                    }}
                  >
                    {
                      (canAccessFullApp || canAccessBoardOnly)
                      && pages.map((page, index) => {
                        const pageIsSelected = isPageSelected(page.path);
                        let buttonVariant = 'text';
                        if (pageIsSelected) {
                          buttonVariant = 'outlined';
                        }

                        let buttonMarginRight = 0;
                        if (index === pages.length - 1) {
                          buttonMarginRight = '12px';
                        }

                        let buttonBackground = 'inherit';
                        if (mode !== 'dark' && pageIsSelected) {
                          buttonBackground = alpha(gray[400], 0.3);
                        }

                        return (
                          <Badge
                            key={page.title}
                            badgeContent={page.badgeCount}
                            variant="dot"
                            invisible={!page.badgeCount}
                            color="error"
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            sx={{
                              '& .MuiBadge-badge': {
                                right: 5,
                                top: 2,
                              },
                            }}
                          >
                            <Button
                              ref={page.ref}
                              variant={buttonVariant}
                              startIcon={page.startIcon || null}
                              endIcon={page.endIcon || null}
                              onClick={(e) => {
                                if (page.onClick) {
                                  page.onClick(e)
                                } else {
                                  navigate(page.path);
                                  handleCloseNavMenu();
                                }
                              }}
                              sx={{
                                color: 'white',
                                '&:hover': hoverText,
                                marginTop: 0,
                                marginBottom: 0,
                                marginRight: buttonMarginRight,
                                px: navButtonPaddingX,
                                paddingY: 0,
                                backgroundColor: buttonBackground,
                              }}
                            >
                              {page.title}
                            </Button>
                          </Badge>
                        );
                      })
                    }
                    {
                      showClientBanner && (
                        <Alert
                          sx={{
                            margin: 'auto',
                            border: 0,
                          }}
                          severity="info"
                          variant="filled"
                        >
                          Download the Questri Mobile App for the best experience!
                        </Alert>
                      )
                    }
                  </Box>
                  <Box
                    sx={{
                      marginRight: navRightMargin,
                      display: {
                        xs: 'none',
                        md: 'flex',
                      },
                      alignItems: 'center',
                    }}
                  >
                    {selectedLocation && !isUnder1000 && moment.tz(contextTimezone).zoneAbbr() !== moment.tz(myTimezone).zoneAbbr() && (
                      <Tooltip
                        title={(
                          <>
                            <div>Your current timezone {moment.tz(myTimezone).zoneAbbr()} ({myTimezone}) is different
                              than this location <b>{moment.tz(selectedLocation.timezone).zoneAbbr()} ({selectedLocation.timezone})</b>.
                            </div>
                            <div>All calendar events are shown in {moment.tz(selectedLocation.timezone).zoneAbbr()} {selectedLocation.timezone}
                              timezone.
                            </div>
                          </>
                        )}
                      >
                        <Box
                          sx={{
                            ':hover': {
                              cursor: 'pointer',
                              opacity: 0.8,
                            },
                            ml: '12px',
                            mr: '16px',
                            flexShrink: 0,
                            position: 'relative',
                            zIndex: 10,
                            display: cstContainerDisplay,
                            alignItems: 'center',
                            justifyContent: cstContainerJustify,
                            width: cstContainerWidth,
                            height: cstContainerHeight,
                            padding: cstContainerPadding,
                            borderRadius: cstContainerRadius,
                          }}
                        >
                          <Chip
                            variant="outlined"
                            icon={<AccessTime />}
                            color="warning"
                            label={cstLabel}
                            size={cstChipSize}
                            sx={{
                              minHeight: 32,
                              minWidth: cstChipMinWidth,
                              px: cstChipPaddingX,
                              py: cstChipPaddingY,
                              padding: cstChipPadding,
                              borderRadius: cstChipRadius,
                              display: 'inline-flex',
                              alignItems: 'center',
                              columnGap: cstChipColumnGap,
                              '& .MuiChip-label': {
                                display: cstChipLabelDisplay,
                                fontSize: cstChipLabelFontSize,
                                fontWeight: 500,
                                lineHeight: 1,
                              },
                              '& .MuiChip-icon': {
                                margin: 0,
                                fontSize: 16,
                                lineHeight: 1,
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                          />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  <Box
                    sx={{
                      marginLeft: 'auto',
                      marginRight: navRightMargin,
                      display: {
                        xs: 'none',
                        md: 'flex',
                      },
                      alignItems: 'center',
                      gap: trialGroupGap,
                    }}
                  >
                    {
                      (
                        trialDaysRemaining !== null
                      )
                      && (
                        <Box
                          sx={{
                            display: {
                              xs: 'none',
                              md: 'flex',
                            },
                            alignItems: 'center',
                          }}
                        >
                          <Tooltip title={`Trial ends in ${trialDaysRemaining} days. If you have already added a payment method, you can ignore this message—your trial will automatically convert to a full paid subscription when the free trial expires. Otherwise, the owner of the Questri Barn should add a payment method from the billing page before the trial ends.`} placement="right">
                            <Chip
                              size="small"
                              color={trialChipColor}
                              label={trialChipLabel}
                              clickable={permissions.owner}
                              onClick={() => { if (permissions.owner) { navigate('/settings/admin'); } }}
                              sx={{
                                minHeight: 32,
                                px: trialChipPaddingX,
                                py: trialChipPaddingY,
                                fontSize: trialChipFontSize,
                              }}
                            />
                          </Tooltip>
                        </Box>
                      )
                    }
                    {
                      canAccessFullApp && (
                        <Search>
                          <SearchIconWrapper>
                            <SearchIcon />
                          </SearchIconWrapper>
                          <StyledInputBase
                            value={search}
                            onChange={(e) => { setSearchAnchorElement(e.target); setSearch(e.target.value); }}
                            placeholder="Search..."
                            inputProps={{ 'aria-label': 'search' }}
                          />

                          <SearchList
                            searchText={search}
                            navigate={navigate}
                            debouncedSearchText={debouncedSearch}
                            handleSearchChange={(e) => setSearch(e.target.value)}
                            anchorElement={searchAnchorElement}
                            additionalQueryParams={additionalSearchQueryParams}
                            onlySearchFiles={onlySearchFiles}
                            searchHorses={searchHorses}
                            searchUsers={searchUsers}
                            searchNotes={searchNotes}
                            onOnlySearchFilesChange={(checked) => setOnlySearchFiles(checked)}
                            onSearchHorsesChange={(checked) => setSearchHorses(checked)}
                            onSearchUsersChange={(checked) => setSearchUsers(checked)}
                            onSearchNotesChange={(checked) => setSearchNotes(checked)}
                            fullScreen={fullScreen}
                            hideSearchResults={() => {
                              setSearchAnchorElement(null);
                              setSearch('');
                            }}
                          />
                        </Search>
                      )
                    }
                  </Box>
                  <Box
                    sx={{ display: { md: 'none' } }}
                  >
                    {selectedLocation && moment.tz(contextTimezone).zoneAbbr() !== moment.tz(myTimezone).zoneAbbr() && (
                      <Tooltip
                        title={(
                          <>
                            <div>Your current timezone {moment.tz(myTimezone).zoneAbbr()} ({myTimezone}) is different
                              than this location <b>{moment.tz(selectedLocation.timezone).zoneAbbr()} ({selectedLocation.timezone})</b>.
                            </div>
                            <div>All calendar events are shown in {moment.tz(selectedLocation.timezone).zoneAbbr()} {selectedLocation.timezone}
                              timezone.
                            </div>
                          </>
                        )}
                      >
                        <IconButton color="warning">
                          <Icon>access_time</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {
                    canAccessFullApp && chatClient && (
                      <Box
                        sx={{
                          flexGrow: 0,
                          // mr: 1.5,
                        }}
                      >
                        <Tooltip title="View and send Questri Chat messages (or chat with Questri Support)">
                          <IconButton
                            sx={{
                              color: 'white',
                              '&:hover': hoverText,
                              // backgroundColor: (mode === 'dark')
                              //   ? 'inherit'
                              //   : alpha(gray[400], 0.3),
                              border: 'none',
                              background: 'transparent !important',
                            }}
                            onClick={(e) => { setChatAnchorEl(e.currentTarget); setShowChat(true); }}
                          >
                            <Badge badgeContent={unreadCount} color="error">
                              <Chat />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                        <NotificationList
                          anchorElement={notificationAnchorElement}
                          fullScreen={fullScreen}
                          onClose={() => setNotificationAnchorElement(null)}
                        />
                      </Box>
                    )
                  }
                  {
                    canAccessFullApp && (
                      <Box
                        sx={{
                          flexGrow: 0,
                          // mr: 1.5,
                        }}
                      >
                        <Tooltip title="View notifications">
                          <IconButton
                            sx={{
                              color: 'white',
                              '&:hover': hoverText,
                              // backgroundColor: (mode === 'dark')
                              //   ? 'inherit'
                              //   : alpha(gray[400], 0.3),
                              border: 'none',
                              background: 'transparent !important',
                            }}
                            onClick={handleNotificationButtonClick}
                          >
                            <Badge
                              badgeContent={unviewedNotificationCount}
                              color="error"
                              invisible={!unviewedNotificationCount}
                            >
                              <Notifications />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                        <NotificationList
                          anchorElement={notificationAnchorElement}
                          fullScreen={fullScreen}
                          onClose={() => setNotificationAnchorElement(null)}
                        />
                      </Box>
                    )
                  }

                  {
                    canAccessFullApp && (
                      <Box
                        sx={{
                          flexGrow: 0,
                          mr: 1,
                        }}
                      >
                        <Tooltip title="Questri settings">
                          <IconButton
                            sx={{
                              color: 'white',
                              '&:hover': hoverText,
                              // backgroundColor: (mode === 'dark')
                              //   ? 'inherit'
                              //   : alpha(gray[400], 0.3),
                              border: 'none',
                              background: 'transparent !important',
                            }}
                            onClick={() => {
                              navigate('/settings/feeds');
                            }}
                          >
                            <Settings />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )
                  }

                  <Box sx={{ flexGrow: 0 }}>
                    <Tooltip title="Profile options">
                      <Box
                        onClick={handleOpenUserMenu}
                        sx={{
                          p: 0,
                          display: 'flex',
                          '&:hover': {
                            cursor: 'pointer',
                            opacity: 0.8,
                          },
                        }}
                      >
                        {(!isFetchingUser && barnUser)
                          ? <User mini user={barnUser} cacheBust />
                          : (
                            <Avatar
                              src={authUser && (authUser['https://pingum.io/avatar'] || authUser.picture)}
                              sx={{
                                width: '2rem !important',
                                height: '2rem !important',
                              }}
                            />
                          )}
                      </Box>
                    </Tooltip>
                    <Menu
                      sx={{ mt: '45px' }}
                      id="menu-appbar"
                      anchorEl={anchorElUser}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      open={Boolean(anchorElUser)}
                      onClose={handleCloseUserMenu}
                    >
                      {profileSetting && (!isFetchingUser && barnUser) && (
                        <Box key={`settings-link-${profileSetting.name}`}>
                          <MenuItem
                            onClick={(event) => handleUserMenuSelection(event, profileSetting)}
                          >
                            {profileSetting.icon}
                            <Typography textAlign="center" sx={{ ml: 1 }}>{profileSetting.name}</Typography>
                          </MenuItem>
                          {profileSetting.divider && <Divider sx={{ m: 1 }} />}
                        </Box>
                      )}
                      <Box key="settings-link-dark-mode">
                        <MenuItem
                          disableRipple
                          onClick={(event) => { event.stopPropagation(); }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                          }}
                        >
                          <Tooltip
                            title="Dark mode is currently in beta. Some features may not be fully styled."
                            placement="right"
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <DarkMode color="secondary" />
                              <Typography
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                Dark Mode
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: 'rgba(0, 188, 212, 0.2)',
                                    color: '#00bcd4',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    letterSpacing: '0.4px',
                                  }}
                                >
                                  BETA
                                </Box>
                              </Typography>
                            </Box>
                          </Tooltip>
                          <Switch
                            checked={mode === 'dark'}
                            onClick={(event) => { event.stopPropagation(); }}
                            onChange={(event) => {
                              event.stopPropagation();
                              let nextMode = 'light';
                              if (mode === 'light') {
                                nextMode = 'dark';
                              }
                              setMode(nextMode);
                            }}
                          />
                        </MenuItem>
                        <Divider sx={{ m: 1 }} />
                      </Box>
                      {otherSettings.filter((s) => ((!isFetchingUser && barnUser) || s.showOnHome)).map((setting) => (
                        <Box key={`settings-link-${setting.name}`}>
                          <MenuItem
                            onClick={(event) => handleUserMenuSelection(event, setting)}
                          >
                            {setting.icon}
                            <Typography textAlign="center" sx={{ ml: 1 }}>{setting.name}</Typography>
                          </MenuItem>
                          {setting.divider && <Divider sx={{ m: 1 }} />}
                        </Box>
                      ))}
                    </Menu>
                  </Box>
                </>
              )
          }
        </Toolbar>
      </Container>
      <Popover
        open={showChat}
        anchorEl={chatAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid
          container
          sx={{
            width: {
              xs: 360,
              sm: 500,
              md: 800,
            },
          }}
        >
          <Grid size={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '.5em .5em .5em 1em',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  ml: 1,
                }}
              >
                <Chat
                  sx={{ mr: 1 }}
                />
                <Typography variant="h5">
                  Questri Chat
                </Typography>
              </Box>

              <Box>
                <Tooltip title="Go to chat page for more options">
                  <IconButton onClick={handleGoToChatPage} sx={{ mr: 1 }}>
                    <Launch />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close chat">
                  <IconButton onClick={handleClose}>
                    <Close />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider />

          </Grid>
          <Grid sx={{ height: '600px !important' }} size={12}>
            <StreamChat
              client={chatClient}
              defaultLanguage="en"
              theme="team light"
            >
              <Box className="app__wrapper str-chat" sx={{ height: '600px !important' }}>
                <WorkspaceController>
                  <Sidebar popout />
                  <ChannelContainer popout />
                </WorkspaceController>
              </Box>
            </StreamChat>
          </Grid>
        </Grid>
      </Popover>
    </AppBar>
  );
};

AppBarComponent.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  tenantObject: PropTypes.object,
  navigate: PropTypes.func.isRequired,
  chatClient: PropTypes.any,
  unreadCount: PropTypes.number,
};

AppBarComponent.defaultProps = {
  tenantObject: {},
  chatClient: null,
  unreadCount: 0,
};

export default AppBarComponent;
